/**
 * Keeps a document the user attaches in the chat as FILES in the project, so the agent can open it with
 * its tools instead of carrying the whole text in every model call.
 *
 * Layout (project-root-relative, the same in a real project and in the staging one):
 *   brain/inputs/original/<name>   the file exactly as uploaded
 *   brain/inputs/parsed/<name>.md  its Markdown text (already extracted by the chat widget via Docling)
 *
 * On the first message of a conversation there is no project yet (the agent creates it with
 * `vfs_ensure_project`), so the files go to a staging project named after the chat session and
 * `vfs_ensure_project` moves them into the real one — the naming MUST match adk-be-app's
 * `python_vfs_io.inbox_project_id`.
 */

import { createProject, writeBinaryFile, writeFile } from './vfsClient';

export const ORIGINAL_DIR = 'brain/inputs/original';
export const PARSED_DIR = 'brain/inputs/parsed';
const MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;

/** Lowercase, only `[a-z0-9._-]` — vfs-service's project id rule. Mirrors adk-be-app's inbox_project_id. */
export function inboxProjectId(sessionId) {
  const cleaned = String(sessionId)
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, '-');
  return `inbox-${cleaned}`.replace(/^[-.]+|[-.]+$/g, '').slice(0, 120);
}

/** A file name that is safe as a path segment: no slashes or odd characters, never empty. */
export function safeFileName(name) {
  const base = String(name || 'document').split(/[\\/]/).pop();
  const cleaned = base.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[-.]+/, '');
  return cleaned || 'document';
}

function stemOf(fileName) {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
}

const NOTE = [
  'The document is stored in the project — do NOT ask the user to paste it and do not retype it.',
  'To understand it: call `vfs_parse_document` on the original (Docling, returns an outline and a preview),',
  'or work on the Markdown copy: `execute_python_code(inputs=[<markdown path>], outputs=[<result paths>])`',
  'to search, extract and aggregate, or `vfs_read_file(path, offset, limit)` for one section.',
  'Keep intermediate data and results in project files and print only short summaries.',
].join(' ');

const STAGED_NOTE =
  ' There is no project yet: these files are held for this conversation and move into the project when you ' +
  'call `vfs_ensure_project` (its `attached_files` result lists their final paths, which are the same).';

/**
 * Store one attached document. Resolves to what the chat widget needs (`{ note, paths }`) or null when
 * nothing could be stored — the widget then inlines the full text, so a storage failure never loses it.
 *
 * @param {{ attachment: {name: string, extractedText: string}, file: File, projectId?: string, sessionId?: string }} args
 */
export async function storeChatAttachment({ attachment, file, projectId, sessionId }) {
  const targetProject = projectId || (sessionId ? inboxProjectId(sessionId) : null);
  if (!targetProject || !attachment?.extractedText) { return null; }
  const staged = !projectId;
  const fileName = safeFileName(attachment.name || file?.name);
  const originalPath = `${ORIGINAL_DIR}/${fileName}`;
  const parsedPath = `${PARSED_DIR}/${stemOf(fileName)}.md`;
  try {
    if (staged) { await createProject(targetProject); }
    const paths = [];
    if (file && file.size <= MAX_ORIGINAL_BYTES) {
      await writeBinaryFile(targetProject, originalPath, file);
      paths.push(originalPath);
    }
    await writeFile(targetProject, parsedPath, attachment.extractedText);
    paths.push(parsedPath);
    return { note: staged ? NOTE + STAGED_NOTE : NOTE, paths };
  } catch (error) {
    console.warn('[chat] could not store the attachment in the project, sending it inline instead:', error);
    return null;
  }
}

const DEFERRED_NOTE = [
  'The document is stored in the project but NOT converted yet — do NOT ask the user to paste it.',
  'Call `vfs_parse_document` on the original path above: Docling converts it on the server, saves the Markdown',
  'to `brain/inputs/parsed/` and returns its outline and a short preview (never the whole text).',
  'Then work on the saved Markdown: `execute_python_code(inputs=[<markdown path>], outputs=[<result paths>])`',
  'to search, extract and aggregate, or `vfs_read_file(path, offset, limit)` for one section.',
  'Keep intermediate data and results in project files and print only short summaries.',
].join(' ');

/**
 * Store ONLY the original of a binary document (PDF, Word, PowerPoint, Excel…), straight away, so the chat
 * can send the message without waiting for the document to be converted: the agent converts it on the
 * server with `vfs_parse_document`. Resolves to `{ note, paths }` for the widget, or null when it could not
 * be stored (the widget then converts it in the browser as before).
 *
 * @param {{ file: File, projectId?: string, sessionId?: string }} args
 */
export async function deferChatDocument({ file, projectId, sessionId }) {
  const targetProject = projectId || (sessionId ? inboxProjectId(sessionId) : null);
  if (!targetProject || !file || file.size > MAX_ORIGINAL_BYTES) { return null; }
  const staged = !projectId;
  const originalPath = `${ORIGINAL_DIR}/${safeFileName(file.name)}`;
  try {
    if (staged) { await createProject(targetProject); }
    await writeBinaryFile(targetProject, originalPath, file);
    return { note: staged ? DEFERRED_NOTE + STAGED_NOTE : DEFERRED_NOTE, paths: [originalPath] };
  } catch (error) {
    console.warn('[chat] could not store the document in the project, converting it in the browser instead:', error);
    return null;
  }
}
